import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

const FAQ_QA: { qKey: string; aKey: string }[] = [
  { qKey: "faq.q.shipping_time.q", aKey: "faq.q.shipping_time.a" },
  { qKey: "faq.q.shipping_cost.q", aKey: "faq.q.shipping_cost.a" },
  { qKey: "faq.q.cod.q", aKey: "faq.q.cod.a" },
  { qKey: "faq.q.track.q", aKey: "faq.q.track.a" },
  { qKey: "faq.q.returns.q", aKey: "faq.q.returns.a" },
  { qKey: "faq.q.damaged.q", aKey: "faq.q.damaged.a" },
  { qKey: "faq.q.currency.q", aKey: "faq.q.currency.a" },
  { qKey: "faq.q.cards.q", aKey: "faq.q.cards.a" },
  { qKey: "faq.q.gift.q", aKey: "faq.q.gift.a" },
  { qKey: "faq.q.gift_card.q", aKey: "faq.q.gift_card.a" },
  { qKey: "faq.q.loyalty.q", aKey: "faq.q.loyalty.a" },
  { qKey: "faq.q.ambassador.q", aKey: "faq.q.ambassador.a" },
];

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/faq")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["faq.meta.title"] },
      { name: "description", content: DICTS.en["faq.meta.description"] },
      { property: "og:title", content: DICTS.en["faq.meta.title"] },
      { property: "og:description", content: DICTS.en["faq.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/faq` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/faq` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_QA.map((item) => ({
            "@type": "Question",
            name: DICTS.en[item.qKey as keyof typeof DICTS.en],
            acceptedAnswer: {
              "@type": "Answer",
              text: DICTS.en[item.aKey as keyof typeof DICTS.en],
            },
          })),
        }),
      },
    ],
  }),
  component: FaqPage,
});

const CATEGORIES: { titleKey: string; items: { qKey: string; aKey: string }[] }[] = [
  {
    titleKey: "faq.cat_orders",
    items: [
      { qKey: "faq.q.shipping_time.q", aKey: "faq.q.shipping_time.a" },
      { qKey: "faq.q.shipping_cost.q", aKey: "faq.q.shipping_cost.a" },
      { qKey: "faq.q.cod.q", aKey: "faq.q.cod.a" },
      { qKey: "faq.q.track.q", aKey: "faq.q.track.a" },
    ],
  },
  {
    titleKey: "faq.cat_returns",
    items: [
      { qKey: "faq.q.returns.q", aKey: "faq.q.returns.a" },
      { qKey: "faq.q.damaged.q", aKey: "faq.q.damaged.a" },
    ],
  },
  {
    titleKey: "faq.cat_payment",
    items: [
      { qKey: "faq.q.currency.q", aKey: "faq.q.currency.a" },
      { qKey: "faq.q.cards.q", aKey: "faq.q.cards.a" },
    ],
  },
  {
    titleKey: "faq.cat_gifts",
    items: [
      { qKey: "faq.q.gift.q", aKey: "faq.q.gift.a" },
      { qKey: "faq.q.gift_card.q", aKey: "faq.q.gift_card.a" },
    ],
  },
  {
    titleKey: "faq.cat_account",
    items: [
      { qKey: "faq.q.loyalty.q", aKey: "faq.q.loyalty.a" },
      { qKey: "faq.q.ambassador.q", aKey: "faq.q.ambassador.a" },
    ],
  },
];

function FaqPage() {
  const { t } = useLocale();
  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-3xl px-4 pb-24 pt-0 sm:px-6 md:pt-12">
        <p className="t-eyebrow tracking-[var(--tracking-wider)]">{t("faq.eyebrow")}</p>
        <h1 className="mt-4 t-display">{t("faq.title")}</h1>
        <p className="mt-6 max-w-xl text-muted-foreground">{t("faq.subtitle")}</p>

        <div className="mt-12 space-y-12">
          {CATEGORIES.map((cat) => (
            <section key={cat.titleKey}>
              <h2 className="font-serif text-2xl text-foreground">{t(cat.titleKey)}</h2>
              <div className="mt-4 divide-y divide-border/60 border-y border-border/60">
                {cat.items.map((item) => (
                  <FaqItem
                    key={item.qKey}
                    q={t(item.qKey)}
                    a={t(item.aKey)}
                    trackLabel={item.qKey === "faq.q.track.q" ? t("nav.track_order") : undefined}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="gold-rule mt-16" />
        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground">{t("faq.cta_contact")}</p>
          <div className="flex gap-3">
            <a
              href={`https://wa.me/${LECMO_WA_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground transition hover:opacity-90"
            >
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="rounded-full border border-border px-5 py-2 text-sm text-foreground transition hover:border-primary"
            >
              {t("nav.contact")}
            </Link>
            <Link
              to="/track-order"
              className="rounded-full border border-border px-5 py-2 text-sm text-foreground transition hover:border-primary"
            >
              {t("nav.track_order")}
            </Link>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}

function FaqItem({ q, a, trackLabel }: { q: string; a: string; trackLabel?: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-start"
      >
        <span className="text-base text-foreground">{q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-primary transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden text-muted-foreground transition-all ${
          open ? "max-h-96 pb-5 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p>{a}</p>
        {trackLabel && (
          <Link
            to="/track-order"
            tabIndex={open ? undefined : -1}
            className="mt-3 inline-flex min-h-[36px] items-center text-sm text-primary underline-offset-4 hover:underline"
          >
            {trackLabel}
          </Link>
        )}
      </div>
    </div>
  );
}
